import React, { useEffect, useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { useCurrentUser } from '../../hooks/use-current-user';
import * as api from '../../lib/api';
import { UserDetails } from './UserDetails';
import UserDeleteConfirmation from './UserDeleteConfirmation';
import UserPasswordReset from './UserPasswordReset';
import { UserOperationsAssignment } from './UserOperationsAssignment';
import { Table } from '../../components/table/Table';

export default function UserManagement() {
  const { username } = useCurrentUser();
  const [roles, setRoles] = useState();
  const [organizations, setOrganizations] = useState();
  const [farmgate, setFarmgate] = useState();
  const [users, setUsers] = useState();
  const [userToEdit, setUserToEdit] = useState(null);
  const [userToAssign, setUserToAssign] = useState(null);
  const [userToReset, setUserToReset] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const isLoading = !organizations || !roles || !farmgate || !users;

  useEffect(loadUsers, []);
  useEffect(loadOrganizations, []);
  useEffect(loadRoles, []);
  useEffect(loadFarmgate, []);

  return (
    <Box
      sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {buildHeader()}
      {buildTable()}
      {renderEditDialog()}
      {renderDeleteDialog()}
      {renderResetDialog()}
      {renderGrowerDialog()}
    </Box>
  );

  function buildHeader() {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ display: 'inline-block', flex: 1 }} variant="h5">
          User Management
        </Typography>
        <Button
          onClick={() => setUserToEdit({})}
          variant="contained"
          color="primary"
          disabled={isLoading}
        >
          Add New User
        </Button>
      </Box>
    );
  }

  function buildTable() {
    return (
      <Table
        disableVirtualization
        loading={isLoading}
        hideFooter={false}
        rows={users || []}
        getRowId={(row) => row.username}
        columns={buildTableColumns()}
        initialState={{
          pinnedColumns: { right: ['actions'] },
          sorting: {
            sortModel: [{ field: 'username', sort: 'asc' }],
          },
        }}
        localeText={{
          footerTotalRows: 'Total Users:',
        }}
      />
    );
  }

  function renderEditDialog() {
    return userToEdit ? (
      <UserDetails
        user={userToEdit}
        handleSave={save}
        handleClose={() => setUserToEdit(null)}
        farmgate={farmgate}
        roles={roles}
        organizations={organizations}
      />
    ) : null;
  }

  function renderDeleteDialog() {
    return userToDelete ? (
      <UserDeleteConfirmation
        user={userToDelete}
        handleDelete={remove}
        handleClose={() => setUserToDelete(null)}
      />
    ) : null;
  }

  function renderResetDialog() {
    return userToReset ? (
      <UserPasswordReset
        user={userToReset}
        handleResetPassword={(u) =>
          api.resetUserPassword(u.username, u.userStatus)
        }
        handleClose={() => setUserToReset(null)}
      />
    ) : null;
  }

  function renderGrowerDialog() {
    return userToAssign ? (
      <UserOperationsAssignment
        user={userToAssign}
        handleSaveGrowers={api.assignGrowersToUser}
        handleClose={() => setUserToAssign(null)}
      />
    ) : null;
  }

  function loadUsers() {
    setUsers(null);
    api.getUsers().then((u) => {
      setUsers(flattenAttributes(u));
    });
  }

  function loadRoles() {
    api.get('/roles').then(setRoles);
  }

  function loadOrganizations() {
    api.get('/organizations').then(setOrganizations);
  }

  function loadFarmgate() {
    Promise.all([
      api.get('/farmgate/regions'),
      api.get('/farmgate/regional-business-directors'),
      api.get('/farmgate/area-business-managers'),
      api.get('/farmgate/agronomists'),
      api.get('/farmgate/roles'),
    ]).then(
      ([
        regions,
        regionalBusinessDirectors,
        areaBusinessManagers,
        agronomists,
        fgRoles,
      ]) => {
        setFarmgate({
          regions,
          regionalBusinessDirectors,
          areaBusinessManagers,
          agronomists,
          roles: fgRoles,
        });
      },
    );
  }

  function save(user) {
    return api
      .saveUser(user)
      .then(() => setUserToEdit(null))
      .then(loadUsers);
  }

  async function remove(user) {
    await api.deleteUser(user);
    setUsers((current) => current.filter((u) => u.username !== user.username));
  }

  function buildTableColumns() {
    return [
      {
        field: 'username',
        headerName: 'Username',
        flex: 1.25,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.25,
      },
      {
        field: 'roles',
        headerName: 'Role',
        flex: 1,
        valueGetter: ({ row }) =>
          row.roles
            ?.map((r) => r.label)
            .sort()
            .join(', '),
      },
      {
        field: 'region',
        headerName: 'Region',
        flex: 0.75,
        valueGetter: ({ row }) => row.region?.name,
      },
      {
        field: 'userStatus',
        headerName: 'Status',
        flex: 1,
      },
      {
        field: 'lastLoginAt',
        headerName: 'Last Activity',
        flex: 1,
        type: 'dateTime',
        valueGetter: ({ value }) => value && new Date(value),
      },
      {
        field: 'assign',
        headerName: 'Operations',
        sortable: false,
        disableColumnMenu: true,
        width: 110,
        renderCell: ({ row }) =>
          /administrator/i.test(row.role) ? (
            'N/A'
          ) : (
            <Button
              disabled={row.role === 'administrator'}
              onClick={() => setUserToAssign(displayToEdit(row))}
              data-testid={`growers-${row.username}`}
              variant="outlined"
              color="inherit"
            >
              Operations
            </Button>
          ),
      },
      {
        field: 'edit',
        headerName: 'Edit',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            onClick={() => setUserToEdit(displayToEdit(row))}
            data-testid={`edit-${row.username}`}
            variant="outlined"
            color="inherit"
          >
            Edit
          </Button>
        ),
      },
      {
        field: 'reset',
        headerName: 'Reset Password',
        headerAlign: 'left',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            disabled={username === row.username}
            onClick={() => setUserToReset(displayToEdit(row))}
            data-testid={`reset-${row.username}`}
            variant="outlined"
            color="inherit"
          >
            Reset
          </Button>
        ),
      },
      {
        field: 'delete',
        headerName: 'Delete',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            disabled={username === row.username}
            onClick={() => setUserToDelete(displayToEdit(row))}
            data-testid={`delete-${row.username}`}
            variant="outlined"
            color="inherit"
          >
            Delete
          </Button>
        ),
      },
    ];
  }
}

function flattenAttributes(users) {
  return users.map((u) => ({
    ...u,
    ...u.Attributes.reduce((a, b) => ({ ...a, [b.Name]: b.Value }), {}),
    username: u.Username,
    userStatus: u.UserStatus,
  }));
}

function displayToEdit(displayUser) {
  const splitName = (displayUser.name || '').split(' ');
  const splitAddress = (displayUser.address || '').split('||');

  return {
    username: displayUser.username,
    firstName: splitName.length > 0 ? splitName[0] : '',
    lastName: splitName.length > 1 ? splitName[1] : '',
    email: displayUser.email,
    phone: displayUser.phone_number,
    addressLine1: splitAddress.length > 0 ? splitAddress[0] : '',
    addressLine2: splitAddress.length > 1 ? splitAddress[1] : '',
    city: splitAddress.length > 2 ? splitAddress[2] : '',
    state: splitAddress.length > 3 ? splitAddress[3] : '',
    zip: splitAddress.length > 4 ? splitAddress[4] : '',
    userType: displayUser.role || '',
    userStatus: displayUser.userStatus || '',
    sub: displayUser.sub,
    role: displayUser.role,
    roles: displayUser.roles,
    ccaLicenses: displayUser.ccaLicenses,
    organizations: displayUser.organizations,
  };
}
